// ============================================
// CONJUNTO MÍNIMO DO RECEPTOR — B351 passo 5b (AI_153)
// ============================================
// A análise do DOADOR precisa de alguns atributos do RECEPTOR: sem o peso não há
// GRWR (lição 4 do passo 5a), e sem gravidade e diagnóstico não se discute o
// tamanho do enxerto nem o risco. Mas isso CRUZA DOIS PRONTUÁRIOS — tratamento
// de dado NOVO, a registrar para o advogado (como o B294 fez).
//
// Decisão de privacidade de Leonardo (AI_152, confirmada em 05/09/2026): entra o
// CONJUNTO MÍNIMO NOMEADO, nunca o prontuário inteiro, e o receptor NUNCA é
// identificado. Por isso este módulo é uma função PURA com saída FECHADA: quem
// chama não escolhe campos, recebe exatamente estes — o que impede que uma
// sessão futura "só acrescente um campinho".
//
// ⛔ NUNCA SAI: nome, id, CPF, RGCT, CNS, data de nascimento, idade, sexo (usado
//    apenas DENTRO do cálculo do MELD, não devolvido), telefone, endereço, texto
//    livre, exames, imagens, consultas, prescrições, situação especial, posição
//    em lista.
//
// Rótulos de confiança viajam COM o dado (lição 5: número derivado herda a
// confiança da origem) — origem do MELD, validade, componentes suspeitos, e se a
// superfície corporal foi calculada com altura inferida.

import { meldMaisRecenteDeExames, ExameAnonimizadoMeld } from "./meldDeExame";

/** Veredito de compatibilidade ABO — DECISÃO 1: o grupo do receptor não sai. */
export type CompatibilidadeABO = "compativel" | "incompativel" | "nao_informado";

export interface ConjuntoMinimoReceptor {
  /** Peso do CADASTRO do receptor (kg). null se não registrado. */
  peso: number | null;
  /**
   * Peso do receptor DIGITADO na aba do doador (kg), quando diverge do cadastro.
   * Entregue junto, de propósito: uma das duas fontes está desatualizada, e isso
   * importa mais que o número — a IA aponta a divergência em vez de escolher.
   */
  pesoDigitadoNaAba: number | null;
  /** Superfície corporal (m², Mosteller). null se faltar peso ou altura. */
  superficieCorporal: number | null;
  /** A altura foi INFERIDA (cm × m)? Então a SC sai como PROVISÓRIA. */
  superficieCorporalProvisoria: boolean;
  /** MELD 3.0 do exame pré-Tx mais recente em que seja determinável. */
  meld: number | null;
  meldData: string | null;
  meldOrigem: "gravado" | "calculado" | null;
  meldDentroDaValidade: boolean | null;
  meldComponentesSuspeitos: string[];
  /** Etiologia/indicação, uma linha. */
  diagnostico: string | null;
  /**
   * DECISÃO 2 — gravidade como BIT ÚNICO: há descompensação registrada
   * (ascite, encefalopatia ou HDA prévia)? `null` = avaliação clínica ausente,
   * que NÃO é o mesmo que "não". Ausência de registro nunca vira tranquilidade.
   */
  descompensacaoAtual: boolean | null;
  /** DECISÃO 1 — só o veredito; nem o grupo do doador nem o do receptor saem. */
  aboCompativel: CompatibilidadeABO;
}

/**
 * Superfície corporal pela fórmula de Mosteller: √(altura_cm × peso_kg / 3600).
 * É a mais usada na prática e a menos sensível a erro de digitação.
 * `alturaFoiInferida` avisa quando a altura veio em metros e foi convertida —
 * a mesma heurística de `calcularIMC` (>3 ⇒ centímetros).
 */
export function superficieCorporalMosteller(
  pesoKg?: number | null,
  altura?: number | null
): { valor: number | null; alturaFoiInferida: boolean } {
  const p = Number(pesoKg);
  let a = Number(altura);
  if (!Number.isFinite(p) || !Number.isFinite(a) || p <= 0 || a <= 0) {
    return { valor: null, alturaFoiInferida: false };
  }
  let inferida = false;
  if (a <= 3) {
    a = a * 100; // veio em metros
    inferida = true;
  }
  const sc = Math.sqrt((a * p) / 3600);
  return { valor: Math.round(sc * 100) / 100, alturaFoiInferida: inferida };
}

/** Compara os dois tipos sanguíneos e devolve SÓ o veredito (DECISÃO 1). */
export function compatibilidadeABO(
  tipoDoador?: string | null,
  tipoReceptor?: string | null
): CompatibilidadeABO {
  const grupo = (t?: string | null): string | null => {
    const m = String(t || "")
      .trim()
      .toUpperCase()
      .match(/^(AB|A|B|O)/);
    return m ? m[1] : null;
  };
  const d = grupo(tipoDoador);
  const r = grupo(tipoReceptor);
  if (!d || !r) return "nao_informado";
  // Regra clássica do transplante hepático (o Rh não é barreira).
  const podeDoarPara: Record<string, string[]> = {
    O: ["O", "A", "B", "AB"],
    A: ["A", "AB"],
    B: ["B", "AB"],
    AB: ["AB"],
  };
  return podeDoarPara[d]?.includes(r) ? "compativel" : "incompativel";
}

/** Fonte do receptor: só os campos que este módulo pode ler. */
export interface ReceptorParaConjuntoMinimo {
  peso?: number | string | null;
  altura?: number | string | null;
  sexo?: string | null;
  tipoSanguineo?: string | null;
  grupoSanguineo?: string | null;
  inscricaoTx?: { diagnosticoBase?: string | null } | null;
  avaliacaoClinica?: {
    causaCirrose?: string | null;
    ascite?: boolean | null;
    encefalopatia?: boolean | null;
    hdaPrevia?: boolean | null;
  } | null;
}

function num(v: unknown): number | null {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/**
 * Monta o conjunto mínimo. Função PURA: recebe o receptor e os exames dele já
 * carregados, não busca nada.
 *
 * @param receptor        paciente-receptor (do PacientesContext).
 * @param examesReceptor  exames do receptor, para o MELD ao vivo.
 * @param tipoSanguineoDoador  para o veredito ABO (o grupo não é devolvido).
 * @param pesoReceptorDigitado o `pesoReceptor` da aba do doador.
 * @param hoje            injetável para teste.
 */
export function montarConjuntoMinimoReceptor(
  receptor: ReceptorParaConjuntoMinimo | null | undefined,
  examesReceptor: ExameAnonimizadoMeld[] = [],
  tipoSanguineoDoador?: string | null,
  pesoReceptorDigitado?: number | string | null,
  hoje: Date = new Date()
): ConjuntoMinimoReceptor | null {
  // Sem receptor vinculado NÃO se infere nada: GRWR e compatibilidade ficam
  // INDETERMINADOS no texto, que é mais honesto que um número montado.
  if (!receptor) return null;

  const pesoCadastro = num(receptor.peso);
  const pesoAba = num(pesoReceptorDigitado);
  const sc = superficieCorporalMosteller(pesoCadastro, num(receptor.altura));

  const meld = meldMaisRecenteDeExames(
    examesReceptor,
    String(receptor.sexo || "").toUpperCase() === "F",
    hoje
  );

  const ac = receptor.avaliacaoClinica || null;
  const descompensacaoAtual = ac
    ? Boolean(ac.ascite) || Boolean(ac.encefalopatia) || Boolean(ac.hdaPrevia)
    : null;

  return {
    peso: pesoCadastro,
    // Só entrega o da aba quando DIVERGE — senão é ruído.
    pesoDigitadoNaAba:
      pesoAba !== null && pesoCadastro !== null && pesoAba !== pesoCadastro
        ? pesoAba
        : pesoAba !== null && pesoCadastro === null
        ? pesoAba
        : null,
    superficieCorporal: sc.valor,
    superficieCorporalProvisoria: sc.alturaFoiInferida,
    meld: meld?.valor ?? null,
    meldData: meld?.data ?? null,
    meldOrigem: meld?.origem ?? null,
    meldDentroDaValidade: meld ? meld.dentroDaValidade : null,
    meldComponentesSuspeitos: meld?.componentesSuspeitos ?? [],
    diagnostico:
      receptor.inscricaoTx?.diagnosticoBase ||
      receptor.avaliacaoClinica?.causaCirrose ||
      null,
    descompensacaoAtual,
    aboCompativel: compatibilidadeABO(
      tipoSanguineoDoador,
      receptor.tipoSanguineo || receptor.grupoSanguineo
    ),
  };
}
